/**
 * Leviathan Web - Orders Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface OrderItem {
  id: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
  product: {
    name: string;
    sku: string;
  };
}

interface Order {
  id: string;
  orderNumber: string;
  status: string;
  orderType: string;
  orderDate: string;
  requestedDate: string | null;
  total: number;
  customer: {
    id: string;
    name: string;
    customerNumber: string;
  };
  items: OrderItem[];
}

interface Stats {
  total: number;
  draft: number;
  confirmed: number;
  inProgress: number;
  delivered: number;
  todayOrders: number;
  monthRevenue: number;
}

interface StatCardProps {
  label: string;
  value: string | number;
  status?: 'operational' | 'warning' | 'critical';
}

/**
 * Compact stat card for order metrics
 */
function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-2xl font-mono font-bold text-amber"
          style={{
            textShadow: '0 0 10px rgba(255, 149, 0, 0.6)',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

/**
 * Get status color and style
 */
function getStatusStyle(status: string): string {
  switch (status) {
    case 'DRAFT':
      return 'text-text-tertiary bg-text-tertiary/10';
    case 'CONFIRMED':
      return 'text-steel bg-steel/10';
    case 'SCHEDULED':
      return 'text-amber bg-amber/10';
    case 'IN_PROGRESS':
      return 'text-warning-amber bg-warning-amber/10';
    case 'DELIVERED':
      return 'text-success-green bg-success-green/10';
    case 'INVOICED':
      return 'text-steel bg-steel/10';
    case 'CANCELLED':
      return 'text-error-red bg-error-red/10';
    default:
      return 'text-text-secondary bg-text-secondary/10';
  }
}

/**
 * Get order type icon
 */
function getOrderTypeIcon(type: string): string {
  switch (type) {
    case 'DELIVERY':
      return 'DLV';
    case 'PICKUP':
      return 'PKP';
    case 'WILL_CALL':
      return 'WCL';
    case 'EMERGENCY':
      return 'EMG';
    default:
      return 'ORD';
  }
}

/**
 * Format date for display
 */
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const OrdersPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [search, statusFilter]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/stats`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${API_URL}/api/orders?${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
  };

  return (
    <div className="space-y-6 relative">
      {/* Header with geometric frame */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Orders
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">Order Management</p>
            </div>
            <Link
              to="/orders/new"
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Order</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Draft" value={stats.draft} />
          <StatCard label="Confirmed" value={stats.confirmed} status="operational" />
          <StatCard
            label="In Progress"
            value={stats.inProgress}
            status={stats.inProgress > 0 ? 'warning' : undefined}
          />
          <StatCard label="Delivered" value={stats.delivered} />
          <StatCard label="Today" value={stats.todayOrders} />
          <StatCard
            label="Month Revenue"
            value={`$${(stats.monthRevenue / 1000).toFixed(1)}K`}
          />
        </div>
      )}

      {/* Filters Panel */}
      <GeometricPanel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Order #, PO #, customer..."
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="DELIVERED">Delivered</option>
              <option value="INVOICED">Invoiced</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GeometricPanel>

      {/* Orders Table */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono animate-pulse">
                &gt; Loading orders...
              </div>
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono text-lg mb-2">
                &gt; No orders found
              </div>
              <p className="text-text-tertiary font-mono text-sm">
                {search || statusFilter
                  ? 'Try adjusting your search filters'
                  : 'Create your first order to get started'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Order
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Customer
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Items
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Date
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Total
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Status
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order.id}
                      className="border-b border-panel-border hover:bg-amber/5 transition-colors cursor-pointer"
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-mono bg-panel-border px-2 py-1 text-steel">
                            {getOrderTypeIcon(order.orderType)}
                          </span>
                          <div>
                            <div className="text-amber font-mono font-semibold hover:text-amber/80">
                              {order.orderNumber}
                            </div>
                            <div className="text-text-tertiary font-mono text-xs">
                              {order.orderType}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-text-primary font-mono">{order.customer.name}</div>
                          <div className="text-text-tertiary font-mono text-xs">
                            {order.customer.customerNumber}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-text-secondary font-mono text-sm">
                          {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                        </div>
                        <div className="text-text-tertiary font-mono text-xs">
                          {order.items
                            .slice(0, 2)
                            .map((item) => item.product.name)
                            .join(', ')}
                          {order.items.length > 2 && '...'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-text-secondary font-mono text-sm">
                          {formatDate(order.orderDate)}
                        </div>
                        {order.requestedDate && (
                          <div className="text-text-tertiary font-mono text-xs">
                            Req: {formatDate(order.requestedDate)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div
                          className="text-amber font-mono font-semibold"
                          style={{
                            textShadow: '0 0 10px rgba(255, 149, 0, 0.4)',
                          }}
                        >
                          ${order.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`${getStatusStyle(order.status)} font-mono text-xs uppercase tracking-wider font-semibold px-2 py-1 rounded`}
                        >
                          {order.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center space-x-2">
                          {order.status === 'CONFIRMED' && (
                            <button
                              onClick={() => navigate(`/dispatch/new?orderId=${order.id}`)}
                              className="text-amber hover:text-amber/80 font-mono text-sm"
                              title="Create Dispatch"
                            >
                              🚚
                            </button>
                          )}
                          {order.status === 'DELIVERED' && (
                            <button
                              onClick={() => navigate(`/invoices/new?orderId=${order.id}&customerId=${order.customer.id}`)}
                              className="text-success-green hover:text-success-green/80 font-mono text-sm"
                              title="Create Invoice"
                            >
                              📄
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/orders/${order.id}/edit`)}
                            className="text-text-secondary hover:text-amber font-mono text-sm"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => navigate(`/orders/${order.id}`)}
                            className="text-steel hover:text-amber font-mono text-sm"
                            title="View"
                          >
                            👁️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
